import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { EntityLookup, type EntityLookupValue } from "@/components/shared/EntityLookup";
import { downloadBlob } from "@/lib/download";
import { gerarBackupCsv } from "./api";

// Porte de app/controllers/exportacao-backup-csv.controller.js +
// app/templates/relatorios/exportacao-backup-csv.html. Primeira rota a usar
// o <EntityLookup> (Fase 4) e o download via blob.
export default function ExportacaoBackupCsv() {
  const [instituicao, setInstituicao] = useState<EntityLookupValue | null>(null);
  const [erros, setErros] = useState<string[] | null>(null);

  const gerar = useMutation({
    mutationFn: () => gerarBackupCsv(instituicao!.id),
    onSuccess: (result) => {
      downloadBlob(result.blob, result.filename);
      setErros(result.erros);
      if (result.decodeFalhou) {
        toast.warning("Backup gerado, mas não foi possível ler a lista de pendências.");
      } else if (result.erros && result.erros.length > 0) {
        toast.warning(`Backup gerado com pendências: ${result.erros.length} relatório(s) não puderam ser gerados.`);
      } else {
        toast.success("Backup gerado com sucesso.");
      }
    },
    onError: () => {
      setErros(null);
      toast.error("Falha ao gerar o arquivo de backup CSV.");
    },
  });

  function handleGerar() {
    if (!instituicao) {
      toast.warning("Selecione a instituição antes de gerar o arquivo.");
      return;
    }
    setErros(null);
    gerar.mutate();
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-foreground">Exportação de Backup em CSV</h1>
      <p className="text-sm text-muted-foreground">
        Selecione a instituição e clique em Gerar para baixar um .zip com os dados da instituição em CSV.
      </p>

      <div className="max-w-sm">
        <EntityLookup
          id="instituicao"
          label="Instituição"
          entityPath="instituicao/Instituicao"
          searchByCodeAndName
          displayCode
          value={instituicao}
          onChange={setInstituicao}
          required
        />
      </div>

      <Button onClick={handleGerar} disabled={gerar.isPending}>
        <Download className="mr-2 h-4 w-4" />
        {gerar.isPending ? "Gerando arquivo..." : "Gerar"}
      </Button>

      {erros && erros.length > 0 && (
        <div className="text-sm">
          <strong>Relatórios não gerados ({erros.length}):</strong>
          <ul className="list-disc pl-5">
            {erros.map((erro, i) => (
              <li key={i}>{erro}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
